import { Reveal, MaskLines } from "./Reveal";
import { Photo } from "./Photo";
import { CtaButton } from "./CtaButton";
import { IMAGES } from "@/constants/images";
import { CandlestickChart, Activity, Layers, LineChart, Coins, Bitcoin } from "lucide-react";

const DISCIPLINES = [
  { icon: CandlestickChart, name: "Day Trading", note: "Intraday execution, tape reading & risk control." },
  { icon: Activity, name: "Swing Trading", note: "Multi-day positioning without the screen addiction." },
  { icon: Layers, name: "Options", note: "Structure, premium and defined-risk conviction." },
  { icon: LineChart, name: "Futures", note: "Index & commodity futures with clean rules." },
  { icon: Coins, name: "Equities", note: "Building durable, scalable stock strategies." },
  { icon: Bitcoin, name: "Bitcoin & Crypto", note: "Trading digital assets through every cycle." },
];

export const Training = () => (
  <section id="work" className="relative border-t border-white/10 py-24 sm:py-32">
    <div
      className="pointer-events-none absolute left-0 top-1/3 h-[55vh] w-[55vh] rounded-full opacity-[0.1] blur-3xl"
      style={{ background: "radial-gradient(circle, #d4af37, transparent 65%)" }}
    />
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <Reveal>
        <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">
          Chapter 03 &mdash; The Work
        </span>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        {/* Portrait + her own words */}
        <Reveal className="lg:col-span-5">
          <div className="corner-frame relative">
            <div className="absolute -inset-3 border border-[#d4af37]/40" />
            <div className="absolute -inset-1.5 border border-[#d4af37]/15" />
            <Photo
              testid="work-portrait"
              src={IMAGES.work}
              alt="Shabz Fazl coaching traders"
              className="aspect-[4/5] w-full object-top"
            />
          </div>
          <blockquote className="mt-8 border-l-2 border-[#d4af37] pl-6">
            <p className="font-['Playfair_Display'] text-xl italic leading-snug text-[#f5f5f0] sm:text-2xl">
              &ldquo;You&rsquo;ve been trying to fix your trading by learning more. But the gap
              between knowing and doing isn&rsquo;t a knowledge problem.&rdquo;
            </p>
            <cite className="mt-3 block font-['IBM_Plex_Mono'] text-[10px] not-italic uppercase tracking-[0.28em] text-[#a1a1aa]">
              &mdash; Shabz Fazl
            </cite>
          </blockquote>
        </Reveal>

        {/* The offer */}
        <div className="lg:col-span-7">
          <h2 className="max-w-2xl font-['Playfair_Display'] text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
            <MaskLines
              whileInView
              lineClass="gold-metallic-text"
              lines={["Private coaching from a", "6 figure stock trading coach."]}
            />
          </h2>

          <Reveal delay={0.15}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-[#a1a1aa] sm:text-lg">
              This isn&rsquo;t another course to add to the pile. It&rsquo;s direct, high-touch
              mentorship built around how <em className="not-italic text-[#f5f5f0]">you</em> trade
              &mdash; your instrument, your timeframe, your psychology. Shabz coaches serious traders
              across every major discipline:
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2">
            {DISCIPLINES.map((d, i) => (
              <Reveal key={d.name} delay={i * 0.08}>
                <div
                  data-testid={`discipline-${i}`}
                  className="gold-toptrim flex h-full items-start gap-4 bg-[#0e0e10] p-6 transition-colors duration-300 hover:bg-[#141416]"
                >
                  <d.icon className="mt-0.5 h-5 w-5 shrink-0 text-[#d4af37]" strokeWidth={1.4} />
                  <div>
                    <h3 className="font-['Playfair_Display'] text-lg text-[#f5f5f0]">{d.name}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-[#a1a1aa]">{d.note}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Paid only banner */}
          <Reveal delay={0.2}>
            <div className="gold-trim mt-10 flex flex-col gap-6 p-8 shadow-[0_0_40px_rgba(212,175,55,0.08)] sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#d4af37]">
                  Paid Program &mdash; By Application
                </p>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-[#a1a1aa]">
                  All training is <strong className="text-[#f5f5f0]">paid only</strong>. If you&rsquo;re
                  truly serious about breaking through, message Shabz directly and she&rsquo;ll discuss
                  the investment and whether it&rsquo;s the right fit.
                </p>
              </div>
              <CtaButton testid="work-cta" className="shrink-0" />
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
